import React, { useEffect } from 'react';
import { Box, Card, Heading, InfoIcon } from '@wix/design-system';
import { useWixPatternsContainer } from '@wix/bex-core/react';
import { observer } from 'mobx-react-lite';
import { ExtendedFields, fqdnToString } from '@wix/bex-core';
import {
  CustomFieldsWidgetContextProvider,
  useCustomFieldsWidgetContext,
} from '../CustomFieldsWidgetContext';
import { useCairoEntityPageContextCompat } from '../../providers';
import { useCustomFieldsWidget } from '../../hooks/useCustomFieldsWidget';
import { CardContainer, CardContainerProps } from '../CardContainer';
import { useWidgetPageStatus } from '../useWidgetPageStatus';

export interface CustomFieldsWidgetProps {
  /**
   * The FQDN to be used to extract schema from data-extension service.
   */
  fqdn?: string;

  /**
   * Applies a data-hook HTML attribute that can be used in tests
   */
  dataHook?: string;

  /**
   * Custom fields widget subtitle.
   * */
  subtitle?: string;

  /**
   * Existing extended fields values, usually under `myEntity.extendedFields`.
   */
  extendedFields?: ExtendedFields;

  hideEmptyState?: boolean;

  /**
   * The fieldName in the object passed to the `onSave` callback.
   */
  fieldName?: string;

  /**
   * Empty state content overrides
   */
  emptyStateOverrides?: {
    /**
     * Empty state title.
     * */
    title?: string;

    /**
     * Empty state subtitle.
     * */
    subtitle?: string;
  };
}

export interface CustomFieldsWidgetBaseProps extends CustomFieldsWidgetProps {
  status: CardContainerProps['status'];
}

const CustomFieldsWidgetContent = ({
  extendedFields,
  emptyStateOverrides,
}: {
  extendedFields: CustomFieldsWidgetBaseProps['extendedFields'];
  emptyStateOverrides: CustomFieldsWidgetBaseProps['emptyStateOverrides'];
}) => {
  const state = useCustomFieldsWidgetContext();
  const CardContent = state.CustomFieldsWidgetContent!;

  useEffect(() => {
    return state.initContent();
  }, []);

  return (
    <div
      ref={(widgetContentRef) => {
        state.setWidgetContentRef(widgetContentRef);
      }}
    >
      <CardContent
        state={state}
        extendedFields={extendedFields}
        emptyStateOverrides={emptyStateOverrides}
      />
    </div>
  );
};

const _CustomFieldsWidgetBase = ({
  subtitle,
  dataHook,
  extendedFields,
  hideEmptyState,
  emptyStateOverrides = {},
  status,
}: CustomFieldsWidgetBaseProps) => {
  const container = useWixPatternsContainer();
  const { translate: t } = container;

  const state = useCustomFieldsWidgetContext();

  if (
    hideEmptyState &&
    (state.isLoading || state.dataExtension.allUserCustomFields.length === 0)
  ) {
    return null;
  }

  const headerSubtitle =
    subtitle || t('cairo.customFields.entityPage.subtitle');
  const headerTitle = (
    <Box gap="SP1">
      <Heading appearance="H3">
        {t('cairo.customFields.entityPage.title')}
      </Heading>
      {state.widgetSize !== 'default' ? (
        <InfoIcon dataHook="subtitle-info-icon" content={headerSubtitle} />
      ) : null}
    </Box>
  );

  const ActionsButton = state.ActionButtonsComponent;
  const suffix = ActionsButton ? <ActionsButton state={state} /> : null;

  return (
    <CardContainer
      stretchVertically
      dataHook={dataHook}
      status={status}
      errorState={{
        dataHook: 'error-state',
        retryText: t('cairo.error.tryAgain.cta'),
        title: t('cairo.customFields.technical.error.title'),
        subtitle: t('cairo.technical.error.text'),
        onRetry: state.retry,
      }}
    >
      <Card.Header
        dataHook="custom-fields-widget-header"
        title={headerTitle}
        subtitle={state.widgetSize === 'default' ? headerSubtitle : null}
        suffix={suffix}
      />
      <Card.Divider />
      <Card.Content dataHook="custom-fields-widget-content">
        <CustomFieldsWidgetContent
          extendedFields={extendedFields}
          emptyStateOverrides={emptyStateOverrides}
        />
      </Card.Content>
    </CardContainer>
  );
};

export const CustomFieldsWidgetBase = observer(_CustomFieldsWidgetBase);

const _CustomFieldsWidget = ({
  fqdn = '',
  fieldName,
  ...rest
}: CustomFieldsWidgetProps) => {
  const pageState = useCairoEntityPageContextCompat();
  const customFieldsState = useCustomFieldsWidget({
    fieldName,
    fqdn: pageState?.fqdn ? fqdnToString(pageState?.fqdn) : fqdn,
  });
  const { status } = useWidgetPageStatus(
    customFieldsState.initTask.status.status,
  );

  return (
    <CustomFieldsWidgetContextProvider value={customFieldsState}>
      <CustomFieldsWidgetBase status={status} {...rest} />
    </CustomFieldsWidgetContextProvider>
  );
};

export const CustomFieldsWidget = observer(_CustomFieldsWidget);
